import { ALGORITHM_IDS } from '~/models/schemaVersion'
import type { ApproximateMonth, CalendarDate } from '~/types/dates'
import { unwrapDate } from '~/types/dates'
import type { Kilograms } from '~/types/units'
import { addDays } from './calendar'

/**
 * Goal projections (D-068).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A PROJECTION IS CONTEXT, NEVER A PROMISE AND NEVER A RECOMMENDATION.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * It never becomes a rules-engine candidate, never triggers an action, and never
 * appears in the weekly opinion. It is expressed in weight terms only: it may not
 * imply a guaranteed appearance outcome, and it may not imply a date.
 *
 * Four paths, and only one of them reports reality:
 *
 *   - **conservative, recommended, faster** read the phase's OWN stored target
 *     range — the D-065 defaults as the user may have edited them. Nothing here
 *     invents a rate, so a "faster" path is bounded by the phase's own maximum
 *     and can never propose a rate nobody approved;
 *   - **observed** reports the pace actually being achieved, and appears only
 *     when both seven-day windows are sufficient under D-036. A plan-only
 *     projection cannot tell a user they are three weeks behind it.
 *
 * Pure: no clock, no storage, no formatting. The reference date and the limits
 * are injected, so the same inputs project the same weeks forever.
 */

/**
 * Compounding, not linear extrapolation.
 *
 * The target range is a percentage of body weight **per week**, so a fixed
 * percentage of a falling weight is a shrinking absolute change. Dividing the
 * remaining kilograms by a fixed weekly figure would understate the time
 * remaining — and would understate it most for the users furthest from their
 * goal, the ones the estimate matters most to. So the projection steps week by
 * week, applying the rate to the running weight.
 */
export interface ProjectionLimits {
  /**
   * How far ahead the stepping will look before giving up.
   *
   * **This is a computation bound, not coaching policy.** D-068 introduced no
   * numeric threshold and `rules/thresholds.ts` is deliberately unchanged by
   * this checkpoint; nothing here reads a rate, a rule or a boundary that any
   * decision had to approve. It exists so a rate small enough to round to
   * nothing cannot spin forever, and it is injectable so a test can drive it.
   */
  readonly horizonWeeks: number
}

export const DEFAULT_PROJECTION_LIMITS: ProjectionLimits = {
  /* Ten years. Far beyond any real body project, which is the point: reaching it
     means the rate is effectively zero, and the honest answer is "not on this
     pace" rather than a number with four significant figures of false precision. */
  horizonWeeks: 520,
}

export type ProjectionPathKey = 'conservative' | 'recommended' | 'faster' | 'observed'

/**
 * What one path arrives at.
 *
 * Three of these four are the "it does not reach" states, and they are distinct
 * because they need different words. A rate pointing the wrong way is not the
 * same as no movement at all, and neither is the same as movement too slow to
 * matter — and none of them may be shown as a negative or unbounded duration.
 */
export type PathOutcome =
  | {
      readonly kind: 'reaches'
      readonly weeks: number
      readonly approximateMonth: ApproximateMonth
    }
  | { readonly kind: 'movingAway' }
  | { readonly kind: 'notMoving' }
  | { readonly kind: 'beyondHorizon'; readonly horizonWeeks: number }

export interface ProjectionPath {
  readonly key: ProjectionPathKey
  /** Percent of body weight per week, signed: negative is loss. */
  readonly weeklyRatePercent: number
  readonly outcome: PathOutcome
}

export type ProjectionDirection = 'loss' | 'gain'

export interface ProjectionTarget {
  readonly weightKg: Kilograms
  /** Which bound of the goal range this is — the one first reached. */
  readonly bound: 'min' | 'max'
  readonly direction: ProjectionDirection
}

/**
 * Why there is no projection.
 *
 * Each is said out loud rather than rendered as a blank card. D-068 requires
 * that a projection which cannot be produced states what is missing.
 */
export type ProjectionUnavailableReason =
  | 'noActivePhase'
  | 'noGoalTargetWeight'
  | 'notEnoughWeighIns'

export interface WeekSpan {
  readonly fastestWeeks: number
  readonly slowestWeeks: number
}

export type Projection =
  | {
      readonly kind: 'unavailable'
      readonly reason: ProjectionUnavailableReason
      readonly algorithm: typeof ALGORITHM_IDS.projection
    }
  | {
      readonly kind: 'alreadyWithinTarget'
      readonly anchorKg: Kilograms
      readonly algorithm: typeof ALGORITHM_IDS.projection
    }
  | {
      readonly kind: 'projected'
      readonly anchorKg: Kilograms
      readonly target: ProjectionTarget
      /** Conservative, recommended and faster, in that order. */
      readonly plan: readonly ProjectionPath[]
      /** Present only when both seven-day windows are sufficient (D-036). */
      readonly observed: ProjectionPath | undefined
      /** Across the plan paths that reach the target. Absent when none do. */
      readonly span: WeekSpan | undefined
      readonly algorithm: typeof ALGORITHM_IDS.projection
    }

export interface ProjectionInput {
  /**
   * The weight the projection compounds from.
   *
   * **The seven-day average, never a single weigh-in.** A projection anchored on
   * one reading would swing by weeks on a salty dinner, which is exactly the
   * day-to-day overreaction D-036 exists to prevent. The caller supplies the
   * current window's average and passes `undefined` when there is not one; there
   * is deliberately no fallback to the latest reading.
   */
  readonly anchorKg: Kilograms | undefined
  readonly targetWeightMinKg: Kilograms | undefined
  readonly targetWeightMaxKg: Kilograms | undefined
  /** The phase's stored range, signed, percent of body weight per week. */
  readonly phaseRange: { readonly minPercent: number; readonly maxPercent: number } | undefined
  /**
   * The rate actually observed, from the week-on-week comparison.
   *
   * Undefined unless both windows are sufficient — the calculation does not
   * re-derive that condition, it trusts the caller not to supply a comparison
   * D-036 withheld.
   */
  readonly observedWeeklyRatePercent: number | undefined
  readonly reference: CalendarDate
}

export function projectGoal(
  input: ProjectionInput,
  limits: ProjectionLimits = DEFAULT_PROJECTION_LIMITS,
): Projection {
  const algorithm = ALGORITHM_IDS.projection

  /* ORDER MATTERS, and it is not the order these arrive in.
   *
   * The two setup gaps are reported BEFORE the waiting one. "Weigh in on a few
   * more days" is useless advice to someone who has never set a target weight:
   * they would weigh in for a week and still get nothing, having been told the
   * opposite. A missing target or phase is fixed by a decision and never by
   * waiting, so it is named first — found in a real browser, where a fresh
   * install has neither. */
  if (input.targetWeightMinKg === undefined && input.targetWeightMaxKg === undefined) {
    return { kind: 'unavailable', reason: 'noGoalTargetWeight', algorithm }
  }
  if (input.phaseRange === undefined) {
    return { kind: 'unavailable', reason: 'noActivePhase', algorithm }
  }
  if (input.anchorKg === undefined) {
    return { kind: 'unavailable', reason: 'notEnoughWeighIns', algorithm }
  }

  const target = resolveTarget(input.anchorKg, input.targetWeightMinKg, input.targetWeightMaxKg)
  if (target === undefined) {
    return { kind: 'alreadyWithinTarget', anchorKg: input.anchorKg, algorithm }
  }

  const rates = planRates(input.phaseRange, target.direction)
  const plan = rates.map(({ key, weeklyRatePercent }) => ({
    key,
    weeklyRatePercent,
    outcome: stepToTarget(input.anchorKg as Kilograms, target, weeklyRatePercent, {
      reference: input.reference,
      limits,
    }),
  }))

  const observed =
    input.observedWeeklyRatePercent === undefined
      ? undefined
      : {
          key: 'observed' as const,
          weeklyRatePercent: input.observedWeeklyRatePercent,
          outcome: stepToTarget(input.anchorKg, target, input.observedWeeklyRatePercent, {
            reference: input.reference,
            limits,
          }),
        }

  return {
    kind: 'projected',
    anchorKg: input.anchorKg,
    target,
    plan,
    observed,
    span: spanOf(plan),
    algorithm,
  }
}

/**
 * Which bound the user reaches first, and which way they are travelling.
 *
 * The nearer edge of the goal range is the milestone: entering the range is the
 * event worth projecting, not crossing it. A weight already inside the range —
 * or already past a lone bound — has nothing to project, and says so rather than
 * reporting zero weeks.
 */
function resolveTarget(
  anchorKg: Kilograms,
  minKg: Kilograms | undefined,
  maxKg: Kilograms | undefined,
): ProjectionTarget | undefined {
  const anchor = anchorKg as number

  if (maxKg !== undefined && anchor > (maxKg as number)) {
    return { weightKg: maxKg, bound: 'max', direction: 'loss' }
  }
  if (minKg !== undefined && anchor < (minKg as number)) {
    return { weightKg: minKg, bound: 'min', direction: 'gain' }
  }
  return undefined
}

/**
 * The three plan rates, ordered by progress **toward the target** rather than by
 * magnitude.
 *
 * This is the same trap Checkpoint 2.4 hit with `ratePosition`: a maintenance
 * range straddles zero, so "the bigger number" and "the faster path" are not the
 * same question. Ordering by magnitude would make −0.25%/week and +0.25%/week
 * indistinguishable, and would hand someone trying to lose weight a "faster"
 * path that gains it.
 *
 * Ordering by direction of travel gives the honest answer instead: under
 * maintenance, the path that gains weight while the goal is below simply reports
 * `movingAway`, which is what D-068 requires it to say.
 */
function planRates(
  range: { readonly minPercent: number; readonly maxPercent: number },
  direction: ProjectionDirection,
): readonly { key: ProjectionPathKey; weeklyRatePercent: number }[] {
  const fastest = direction === 'loss' ? range.minPercent : range.maxPercent
  const slowest = direction === 'loss' ? range.maxPercent : range.minPercent

  return [
    { key: 'conservative', weeklyRatePercent: slowest },
    { key: 'recommended', weeklyRatePercent: (range.minPercent + range.maxPercent) / 2 },
    { key: 'faster', weeklyRatePercent: fastest },
  ]
}

/**
 * Step week by week until the target is reached (D-068).
 *
 * The loop is the decision: `weight *= 1 + rate / 100` each week, so the
 * absolute change shrinks as the weight does. The first week whose end weight
 * has reached the target is the answer, which makes the result a whole number of
 * weeks — a projection accurate to the day would be a false precision the
 * underlying trend cannot support.
 */
function stepToTarget(
  anchorKg: Kilograms,
  target: ProjectionTarget,
  weeklyRatePercent: number,
  context: { readonly reference: CalendarDate; readonly limits: ProjectionLimits },
): PathOutcome {
  if (!Number.isFinite(weeklyRatePercent) || weeklyRatePercent === 0) {
    return { kind: 'notMoving' }
  }
  if (target.direction === 'loss' ? weeklyRatePercent > 0 : weeklyRatePercent < 0) {
    return { kind: 'movingAway' }
  }

  const targetKg = target.weightKg as number
  const factor = 1 + weeklyRatePercent / 100
  let weight = anchorKg as number

  for (let weeks = 1; weeks <= context.limits.horizonWeeks; weeks += 1) {
    weight *= factor
    const reached = target.direction === 'loss' ? weight <= targetKg : weight >= targetKg
    if (reached) {
      return { kind: 'reaches', weeks, approximateMonth: monthAfterWeeks(context.reference, weeks) }
    }
  }

  return { kind: 'beyondHorizon', horizonWeeks: context.limits.horizonWeeks }
}

/** The month a number of weeks from the reference date — year and month only. */
function monthAfterWeeks(reference: CalendarDate, weeks: number): ApproximateMonth {
  const iso = unwrapDate(addDays(reference, weeks * 7))
  return { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) }
}

/**
 * The span the interface states, across the plan paths that actually reach.
 *
 * The width is where the uncertainty lives — "about 10–16 weeks" says what a
 * single number cannot. Paths that never reach contribute nothing rather than
 * collapsing the span to a misleadingly tight one.
 */
function spanOf(plan: readonly ProjectionPath[]): WeekSpan | undefined {
  const weeks = plan
    .map((path) => (path.outcome.kind === 'reaches' ? path.outcome.weeks : undefined))
    .filter((value): value is number => value !== undefined)

  if (weeks.length === 0) return undefined
  return { fastestWeeks: Math.min(...weeks), slowestWeeks: Math.max(...weeks) }
}
